import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Col, Container, Row, Spinner } from "reactstrap";
import ChosenPatient from '../../components/chosenPatient';
import ConsultationNotes from './components/consultationsNotes';
import AppointmentsService from '../../services/appointments';
import { useUpdateChosenPatientContext } from '../Dashboard/tableContext';
import { useHistory, useLocation } from 'react-router-dom';
import queryParser from '../../helpers/queryStringParser';
import VideoComponent from './videoComponent';
import FeedbackModal from './components/feedBackModal';
import CallsService from '../../services/calls';
import moment from 'moment';
import ErrorPlaceholder from './components/errorPlaceholder';
import useMediaDevices from '../../helpers/useMediaDevices';
import generateBitMask from '../../helpers/generateBitmask';
import StorageService from '../../services/StorageService';
import { CALL_VARIABLES, NOTES_VARIABLES } from '../../constants/storageConstants';
import {setChildIfExists} from "../../helpers/childBuilder";

function Call() {

    const location = useLocation();
    const setChosenPatient = useUpdateChosenPatientContext();
    // Token, room and join attempt live in ONE state object on purpose. A rejoin replaces all three
    // from an async callback, which React 16 does not batch - as separate states each setter would
    // trigger its own connect, and those same-identity connections race: whichever joins last
    // throws the others out with a duplicate-identity error, possibly the one meant to survive.
    const [callSession, setCallSession] = useState(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [appointment, setAppointment] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAgreedToPrescription, setIsAgreedToShareInfo] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [documentsToFill, setDocumentsToFill] = useState([]);

    const formRef = useRef();

    const { id } = queryParser(location.search);
    const history = useHistory();

    const { isAllDevices } = useMediaDevices(true);

    const duration = useMemo(
        () => moment(appointment?.endsAt).diff(appointment?.startsAt),
        [appointment]
    );

    const getRoomAndToken = useCallback(
        async () => {
            try {
                const serverResponse = await CallsService.startCall(id);
                setCallSession({
                    token: serverResponse.data.token,
                    roomSid: serverResponse.data.roomSid,
                    attempt: 0,
                });
                const { data } = await AppointmentsService.getAppointment(id);
                setChosenPatient(setChildIfExists(data));
                setAppointment(data);
            } finally {
                setIsLoading(false);
            }
        },
        [id, setChosenPatient],
    )

    // Back into the SAME call after this clinician's connection was lost. Asks the server again, which
    // mints a fresh token and returns the room that is still open for the patient (or a new one if it
    // has since closed). joinAttempt forces a reconnect even if nothing else changed.
    const rejoinCall = useCallback(
        async () => {
            const serverResponse = await CallsService.startCall(id);
            setCallSession(previous => ({
                token: serverResponse.data.token,
                roomSid: serverResponse.data.roomSid,
                attempt: (previous?.attempt || 0) + 1,
            }));
        },
        [id],
    )

    async function sendFeedback({
        videoQuality,
        soundQuality,
        microQuality,
        connectQuality,
        connentToPatient,
        connectToDoc,
        ...values }) {

        const issuesMask = generateBitMask([videoQuality, soundQuality, microQuality, connectQuality, connentToPatient, connectToDoc]);
        const feedbackValues = {
            issuesMask,
            ...values,
        }
        await CallsService.sendFeedback(id, feedbackValues);
        setShowFeedbackModal(false);
        history.push("/appointments");
    }

    useEffect(() => {
        getRoomAndToken();
    }, [getRoomAndToken]);


    useEffect(() => {
        return () => {
            new StorageService().deleteValueByKey(CALL_VARIABLES);
            new StorageService().deleteValueByKey(NOTES_VARIABLES);
        }
    }, []);

    return (
        <div className="calls-page-content">
            <Container fluid>
                <ChosenPatient showPhoneFirst openRecordInNewTab />
                <Row>
                    <Col xl={6} className="calls-page-content__call">
                        {callSession && isAllDevices && !isLoading &&
                            <VideoComponent
                                formRef={formRef}
                                roomName={callSession.roomSid}
                                token={callSession.token}
                                appointment={appointment}
                                duration={duration}
                                isAgreedToPrescription={isAgreedToPrescription}
                                setDocumentsToFill={setDocumentsToFill}
                                isSubmitting={isSubmitting}
                                onRejoin={rejoinCall}
                                joinAttempt={callSession.attempt}
                            />
                        }
                        {isLoading &&
                            <div className="patients-table d-flex justify-content-center align-items-center">
                                <Spinner className="mx-auto" color="info" />
                            </div>
                        }
                        {!isAllDevices && !isLoading && <ErrorPlaceholder />}
                    </Col>
                    <Col xl={6}>
                        <ConsultationNotes
                            formRef={formRef}
                            setShowFeedbackModal={setShowFeedbackModal}
                            appointment={appointment}
                            setIsAgreedToShareInfo={setIsAgreedToShareInfo}
                            documentsToFill={documentsToFill}
                            setDocumentsToFill={setDocumentsToFill}
                            setIsSubmitting={setIsSubmitting}
                        />
                    </Col>
                </Row>
            </Container>
            <FeedbackModal visibleModal={showFeedbackModal} handleSubmit={sendFeedback} isDocumentsLoading={false}/>
        </div>
    )
}

export default Call;
